import { useApi } from "@/api";
import { session } from "@/api/session";
import {
  ContentType,
  TAppHttpApiV10ControllerAccountUpdatePayload,
  TFeature,
  TUser,
} from "@/api/v1_0";
import { RootStore } from "@/store/root";
import { makeAutoObservable } from "mobx";

export class ProfileStore {
  private _rootStore: RootStore;
  private _user: TUser | null = null;
  private _pro: boolean | undefined = undefined;

  public loading = false;
  public submitting = false;

  constructor(rootStore: RootStore) {
    this._rootStore = rootStore;

    makeAutoObservable(this);
  }

  public set user(value: TUser | null) {
    this._user = value;
  }

  public get user(): TUser | null {
    return this._user;
  }

  public set pro(value: boolean) {
    this._pro = value;
  }

  public get pro(): boolean {
    return this._pro || false;
  }

  public isFeatureEnabled = (expected: Array<TFeature>): boolean => {
    if (!this.user) {
      return false;
    }

    if (!this.user.features) {
      return false;
    }

    return (
      expected.filter((feature) => this.user!.features!.includes(feature))
        .length > 0
    );
  };

  public read = async (): Promise<void> => {
    this._rootStore.logging.track("profile.read");

    try {
      const api = useApi();

      const { data: result } = await api.v10.appHttpApiV10ControllerAccountRead(
        {
          headers: {
            "Content-Type": ContentType.Json,
          },
        }
      );

      this._user = result;
      this._pro = result.pro || false;

      if (result.country) {
        this._rootStore.app.country = result.country;
      }

      if (result.currency) {
        this._rootStore.app.currency = result.currency;
      }

      if (result.language) {
        this._rootStore.app.language = result.language;
      }

      if (result.timeZone) {
      }
    } catch (err) {
      throw this._rootStore.logging.capture(err);
    }
  };

  public update = async (
    values: TAppHttpApiV10ControllerAccountUpdatePayload
  ): Promise<void> => {
    this._rootStore.logging.track("profile.update", values);

    try {
      this.submitting = true;

      const api = useApi();

      const { data: result } =
        await api.v10.appHttpApiV10ControllerAccountUpdate(values, {
          headers: {
            "Content-Type": ContentType.Json,
          },
        });

      this._user = result;

      session.userUuid = result.uuid;

      if (result.country) {
        this._rootStore.app.country = result.country;
      }

      if (result.currency) {
        this._rootStore.app.currency = result.currency;
      }

      if (result.language) {
        this._rootStore.app.language = result.language;
      }

      if (result.timeZone) {
      }
    } catch (err: any) {
      throw this._rootStore.logging.capture(err);
    } finally {
      this.submitting = false;
    }
  };

  public destroy = async (): Promise<void> => {
    this._rootStore.logging.track("profile.destroy");

    try {
      this.submitting = true;

      const api = useApi();

      await api.v10.appHttpApiV10ControllerAccountDelete({
        headers: {
          "Content-Type": ContentType.Json,
        },
      });

      this._rootStore.auth.signOut();
    } catch (err: any) {
      throw this._rootStore.logging.capture(err);
    } finally {
      this.submitting = false;
    }
  };
}
